import 'intl-tel-input/build/css/intlTelInput.css'
import 'intl-tel-input/build/js/utils.js'

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import iti from 'intl-tel-input'
import { normalizePhoneNumber } from 'utils/formatting/normalizePhoneNumber'
import styles from './PhoneInput.module.sass'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props Input properties
 */
export interface Props extends Omit<React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'onChange'> {
  /** The additional classes to append */
  className?: string
  /** intl-tel-input options object */
  options?: iti.Options
  /** Handler for value change event */
  onChange: (value: string) => void
  /** Optional event handler for phone validity updates */
  onValidityChange?: (isValid: boolean, errorMessage: string | undefined) => void
  /** Set the initial country */
  initialCountry: string
}

/**
 * @component   
 * Phone number input that auto-formats text and handles country code displaying.
 * Reworked clone of original TelephoneInput fixing reactivity issues for controlled values.
 * 
 * @example
 * <PhoneInput
 *  value={variable}
 *  onChange={(formattedValue) => console.log(`formattedValue: ${formattedValue}`)}
 *  onValidityChange={({ isValid, errorMessage }) => setIsValid(isValid)}
 * />
 */
export const PhoneInput: React.FC<Props> = (props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const itiPlugin = useRef<iti.Plugin | undefined>(undefined)
  const [isPluginReady, setIsPluginReady] = useState(false)
  const { t } = useTranslation('telephone_input')

  const _getCountrifiedNumber = useCallback((bareNumber: string) => {
    // Sanitize BEFORE formatting so the value written to state/payload can never
    // retain letters or other junk. intl-tel-input's formatNumber silently returns
    // the raw unmodified string on parse failure, so without this the displayed
    // (sanitized) value and the submitted value silently diverge — see ISSUE-2542.
    return intlTelInputUtils.formatNumber(
      normalizePhoneNumber(bareNumber),
      itiPlugin.current?.getSelectedCountryData().iso2 || props.initialCountry,
      intlTelInputUtils.numberFormat.E164
    )
  }, [props.initialCountry])

  useLayoutEffect(() => {
    if (!inputRef.current) return
    if (itiPlugin.current) return

    itiPlugin.current = iti(inputRef.current, {
      initialCountry: props.initialCountry,
    })
    setIsPluginReady(true)
  }, [inputRef, props.initialCountry])

  // Propagate country change from props
  useLayoutEffect(() => {
    if (!itiPlugin.current) return
    if (!props.initialCountry) return
    itiPlugin.current.setCountry(props.initialCountry)
  }, [props.initialCountry])

  // Sync value to plugin, validate, and propagate validity — in a single effect
  // to guarantee validation runs AFTER the plugin has the current value
  const onValidityChangeRef = useRef(props.onValidityChange)
  onValidityChangeRef.current = props.onValidityChange

  useEffect(() => {
    if (!itiPlugin.current) return

    itiPlugin.current.setNumber(normalizePhoneNumber(props.value?.toString() || ''))

    const isValid = itiPlugin.current.isValidNumber()
    const errorCode = itiPlugin.current.getValidationError()
    const errorMessage = t(`errors.${errorCode}`, t(`errors.${4}`, '')) || undefined

    onValidityChangeRef.current?.(isValid, errorMessage)
  }, [props.value, t, isPluginReady])

  useEffect(() => {
    // Any, because otherwise it complaints about unknown custom event - not sure how to extend it without extending all inputs' events
    const handler = (e: any) => {
      props.onChange(_getCountrifiedNumber(e.target?.value))
    }

    const telInput = inputRef.current
    if (!telInput) return

    telInput.addEventListener('countrychange', handler)
    return () => {
      telInput.removeEventListener('countrychange', handler)
    }
  }, [_getCountrifiedNumber, props])

  const { options, value, initialCountry, onValidityChange, ...passedProps } = { ...props }

  return (
    <div className={`${styles.phoneInput} fs-mask`}>
      <input
        {...passedProps}
        type={props.type || 'tel'}
        className={props.className}
        ref={inputRef}
        onChange={(e) => {
          props.onChange(_getCountrifiedNumber(e.target.value))
        }}
        onFocus={(e) => props.onFocus?.(e)}
        onBlur={(e) => {
          e.target.value = e.target.value.replace(/\s+/gmi, ' ').trim()
          props.onBlur?.(e)
        }}
      />
    </div>
  )
}
